import { useEffect, useRef } from "react";

import { datadogRum } from "@datadog/browser-rum";

import { DEFAULT_REPLAY_SAMPLE_RATE, DEFAULT_SAMPLE_RATE } from "../constants";

import { useDatadogUser } from "./use-datadog-user";

import type { User } from "../types";
import type { RumInitConfiguration } from "@datadog/browser-rum";

interface UseDatadogProps
  extends Omit<
    RumInitConfiguration,
    | "enableExperimentalFeatures"
    | "replica"
    // remove deprecated so we dont start usgin them
    | "proxyUrl"
    | "premiumSampleRate"
    | "allowedTracingOrigins"
    | "tracingSampleRate"
    | "replaySampleRate"
    | "sampleRate"
    | "trackInteractions"
  > {
  readonly enabled?: boolean;
  readonly sessionReplayRecording?: boolean;
  readonly user?: User;
}

export const useDatadog = ({
  user,
  enabled,
  sessionReplayRecording,
  sessionReplaySampleRate = DEFAULT_REPLAY_SAMPLE_RATE,
  sessionSampleRate = DEFAULT_SAMPLE_RATE,
  ...rest
}: UseDatadogProps) => {
  const isInitialized = useRef(false);
  // A missing ID or token means the release isn't configured; leave RUM off.
  const canInit = Boolean(enabled && rest.applicationId && rest.clientToken);

  useEffect(() => {
    if (!canInit || isInitialized.current) {
      return;
    }

    isInitialized.current = true;

    datadogRum.init({
      sessionReplaySampleRate,
      sessionSampleRate,
      enableExperimentalFeatures: ["feature_flags"],
      ...rest,
    });
  }, [
    canInit,
    sessionReplaySampleRate,
    sessionSampleRate,
    rest,
    isInitialized,
  ]);

  useEffect((): VoidFunction | undefined => {
    if (!canInit || !sessionReplayRecording) {
      return;
    }

    datadogRum.startSessionReplayRecording();
    return (): void => {
      datadogRum.stopSessionReplayRecording();
    };
  }, [canInit, sessionReplayRecording]);

  useDatadogUser(user);
};
